import { planAtLeast, WORKFLOWS_MIN_PLAN } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, ErrorState, Icon, Skeleton } from "@sagot/ui";
import { useWorkspaceDetails } from "../../../session/workspace-context";
import { useTemplateLibrary } from "./template-options";
import { isTicketTrigger } from "./service/service-facts";
import { serviceDeskBlocked, useServiceDesk } from "./service/use-service-desk";
import { localized, type TemplateEntry } from "./templates";

const W = "engage.workflows";
const SKELETON_CARDS = 6;

/** Whether the workspace's plan includes what the template needs (the plan is unknown while it loads: the server decides). */
export function templateAllowed(entry: TemplateEntry, plan: string | undefined): boolean {
  return plan === undefined || planAtLeast(plan, entry.min_plan);
}

interface TemplateGalleryProps {
  value: string;
  onChange: (id: string) => void;
  /** The "Every template opens as a draft" note (a template-only dialog says it in its subtitle). */
  showNote?: boolean;
}

/** The template library as cards: the trigger it starts on, what it does, and the plan it needs. Every template opens as a draft. */
export function TemplateGallery({ value, onChange, showNote = true }: TemplateGalleryProps) {
  const { t, i18n } = useTranslation();
  const plan = useWorkspaceDetails().data?.plan;
  const serviceDesk = useServiceDesk();
  const library = useTemplateLibrary();
  if (library.isError) return <ErrorState title={t(`${W}.templates.loadFailed`)} onRetry={() => void library.refetch()} retryLabel={t("common.retry")} />;
  if (library.isPending) {
    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" aria-hidden="true">
        {Array.from({ length: SKELETON_CARDS }, (_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {showNote ? <p className="text-xs text-fg-subtle">{t(`${W}.templates.note`)}</p> : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" role="radiogroup" aria-label={t(`${W}.templates.title`)}>
        {library.data.templates.map((entry) => {
          // A ticket template needs the Service Desk: listed and marked when the workspace has no use of it, never offered.
          const needsService = isTicketTrigger(entry.trigger) && serviceDeskBlocked(serviceDesk.state);
          const allowed = templateAllowed(entry, plan) && !needsService;
          return (
            <button
              key={entry.id}
              type="button"
              role="radio"
              aria-checked={value === entry.id}
              disabled={!allowed}
              className="rounded-lg border border-line p-4 text-left hover:border-primary hover:bg-primary-soft aria-checked:border-primary aria-checked:bg-primary-soft disabled:cursor-not-allowed disabled:bg-sunken disabled:text-fg-subtle disabled:hover:border-line disabled:hover:bg-sunken"
              data-testid="template-card"
              onClick={() => { onChange(entry.id); }}
            >
              <span className="flex flex-wrap gap-1.5">
                <Badge tone={entry.kind === "facing" ? "brand" : "neutral"}>{t(`${W}.triggers.${entry.trigger}`)}</Badge>
                {needsService ? (
                  <Badge>
                    <Icon name="lock" className="size-3" />
                    {serviceDesk.state === "locked" ? t(`${W}.planNeeded`, { plan: t(`console.plans.${serviceDesk.minPlan}`) }) : t(`${W}.service.notSwitchedOn`)}
                  </Badge>
                ) : entry.min_plan !== WORKFLOWS_MIN_PLAN || !allowed ? (
                  <Badge>
                    <Icon name={allowed ? "check" : "lock"} className="size-3" />
                    {t(`${W}.planNeeded`, { plan: t(`console.plans.${entry.min_plan}`) })}
                  </Badge>
                ) : null}
              </span>
              <span className="mt-2 block text-sm font-bold">{localized(entry.name, i18n.language)}</span>
              <span className="block text-xs text-fg-subtle">{localized(entry.description, i18n.language)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
